import { useEffect, useRef, useState } from "react";
import { DoorOpen, Users, Store, MapPin } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { getLiveNumbers } from "@/lib/api";

// Animated count-up — runs once when the section scrolls into view.
function useCountUp(target, started, duration = 1200) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started || !target) return;
    // Respect reduced motion: show the real number, skip the animation.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setValue(target);
      return;
    }
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, started, duration]);
  return value;
}

function Stat({ icon: Icon, value, label, sub, testId, started }) {
  const n = useCountUp(value, started);
  return (
    <div className="glass rounded-3xl p-6 md:p-8 text-center" data-testid={testId}>
      <div className="mx-auto w-11 h-11 rounded-2xl bg-neon-cyan/10 text-neon-cyan grid place-items-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <p className="font-display text-4xl md:text-5xl font-extrabold text-white tabular-nums" data-testid={`${testId}-value`}>
        {n.toLocaleString()}
      </p>
      <p className="mt-2 text-sm font-semibold text-zinc-200">{label}</p>
      <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{sub}</p>
    </div>
  );
}

// Partner numbers — hidden until the platform has enough real activity to
// brag about: the whole section stays off while "Rooms joined" is below
// MIN_ROOMS_JOINED (config). Zero-value stats never render individually, and
// nothing here exists in the static HTML, so crawlers never see a 0.
const MIN_ROOMS_JOINED = 100;

export default function PartnerNumbers() {
  const [data, setData] = useState(null);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    getLiveNumbers().then(setData).catch(() => setData(null));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setStarted(true),
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [data]);

  if (!data) return null;
  if ((data.rooms_joined || 0) < MIN_ROOMS_JOINED) return null;

  // Zero-fabrication rule, per-stat: a counter that would show 0 is dropped
  // entirely so the grid never contradicts the headline.
  const stats = [
    { icon: DoorOpen, value: data.checkins_total || 0, label: "Check-ins at the door",
      sub: "Guests scanned in at the door", testId: "partner-number-checkins" },
    { icon: Users, value: data.rooms_joined || 0, label: "Rooms joined",
      sub: "Live rooms across iOS, Android and web", testId: "partner-number-rooms" },
    { icon: Store, value: data.venues_live || 0, label: "Partner venues live",
      sub: "Venues running a live room tonight", testId: "partner-number-venues" },
    { icon: MapPin, value: data.cities_requested || 0, label: "Cities requested",
      sub: "Where people want GateMate next", testId: "partner-number-cities" },
  ].filter((s) => s.value > 0);
  if (stats.length === 0) return null;

  return (
    <section ref={ref} className="relative py-24 md:py-28 overflow-hidden" data-testid="partner-numbers-section">
      <div className="glow-orb w-[420px] h-[420px] bg-neon-cyan/12 top-0 right-0" />
      <div className="container-x relative z-10">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="overline mb-4">Partner numbers</p>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight leading-[0.95]">
            The room is already <span className="text-gradient">filling up</span>.
          </h2>
          <p className="mt-5 text-lg text-zinc-400 leading-relaxed">
            Real counts from the live platform — updated as people check in,
            join rooms, and tell us where to go next.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {stats.map((s) => (
            <Stat key={s.testId} icon={s.icon} value={s.value} started={started}
              label={s.label} sub={s.sub} testId={s.testId} />
          ))}
        </div>
      </div>
    </section>
  );
}
